import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Images } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import BackButton from "../../../../components/buttons/back/BackButton";
import AlbumThumbnailCard from "../../../../components/cards/thumbnails/albums/AlbumThumbnailCard";

import CreatorEditAlbumForm from "../forms/CreatorEditAlbumForm";
import CreatorAlbumPhotosView from "./CreatorAlbumPhotosView";

import { useCreatorAlbums } from "../hooks/useAlbums";

import "./CreatorAlbumsView.css";

export default function CreatorAlbumsView({ username }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const albumId = searchParams.get("a");

  const { albums, loading, error, getCreatorAlbums, deleteCreatorAlbum } =
    useCreatorAlbums(username);

  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [deletingAlbumId, setDeletingAlbumId] = useState(null);

  const albumList = Array.isArray(albums) ? albums : [];

  const errorMessage = typeof error === "string" ? error : error?.message || "";

  useEffect(() => {
    if (username) {
      getCreatorAlbums();
    }
  }, [username]);

  const handleAlbumClick = (selectedAlbumId) => {
    if (!selectedAlbumId) return;

    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set("a", String(selectedAlbumId));
      return params;
    });
  };

  const handleBackToAlbums = () => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.delete("a");
      return params;
    });
  };

  const handleDeleteAlbum = async (selectedAlbumId) => {
    if (!selectedAlbumId || deletingAlbumId !== null) return;

    if (
      !window.confirm(
        "Are you sure you want to delete this album? This cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setDeletingAlbumId(selectedAlbumId);

      const result = await deleteCreatorAlbum(selectedAlbumId);

      if (result === false || result?.success === false) return;

      if (String(albumId) === String(selectedAlbumId)) {
        handleBackToAlbums();
      }
    } catch (deleteError) {
      console.error("Unable to delete album:", deleteError);
    } finally {
      setDeletingAlbumId(null);
    }
  };

  return (
    <ViewContainer>
      <ViewHeader
        title={`My Albums (${albumList.length})`}
        description="Manage your photo albums."
        actions={albumId ? <BackButton onClick={handleBackToAlbums} /> : null}
      />

      {!albumId && (
        <>
          {errorMessage && (
            <div className="creator-album-view__error" role="alert">
              {errorMessage}
            </div>
          )}

          {loading && !albumList.length ? (
            <div className="creator-album-view__loading">Loading albums...</div>
          ) : albumList.length ? (
            <ContentGrid
              items={albumList}
              columns={4}
              tabletColumns={3}
              mobileColumns={2}
              gap={16}
              renderItem={(album) => (
                <AlbumThumbnailCard
                  key={album.albumId ?? album.albumTag}
                  album={album}
                  onClick={handleAlbumClick}
                  showMenu
                  disabled={deletingAlbumId !== null}
                  menuOptions={[
                    {
                      label: "Edit Album",
                      action: () => setSelectedAlbum(album),
                    },
                    {
                      label: "Delete Album",
                      action: () => handleDeleteAlbum(album.albumId),
                    },
                  ]}
                />
              )}
            />
          ) : (
            <EmptyState
              icon={<Images size={28} />}
              title="No albums yet"
              description="Create your first album to share photos with your community."
            />
          )}
        </>
      )}

      {albumId && (
        <CreatorAlbumPhotosView username={username} albumId={albumId} />
      )}

      {selectedAlbum && (
        <CreatorEditAlbumForm
          username={username}
          album={selectedAlbum}
          onClose={() => setSelectedAlbum(null)}
          onRefresh={getCreatorAlbums}
        />
      )}
    </ViewContainer>
  );
}
